import React, { Suspense, useEffect, useState, useMemo } from 'react'
import ReactDOM from 'react-dom'
import {
    TopBar,
    Logo,
    Button,
    DropdownMenu,
    FavouriteNavButton,
    IconButton,
    Icon,
    Media,
} from 'components'
import { Link, useLocation } from 'react-router-dom'
import {
    RootState,
    CustomEventType,
    PageType,
    ScreenSize,
    ScreenOrientation,
    UserRole,
    AdminView,
    PromptOptions,
    AnalyticsEvent,
    PromptType,
    FormFieldType,
} from 'app/types'
import {
    navigateAsync,
    clearCurrent,
    setPath,
    showPrompt,
    initializeTheming,
} from 'actions/appActions'
import {
    logoutUser,
} from 'actions/userActions'
import { useAppSelector, useAppDispatch, useAdminPermissions, usePage } from 'app/hooks'
import * as fnc from 'helpers/fnc'
import { isHttpUrl, marketingFormOptions, marketingScheduleDestination, reportMarketingEvent, reportMarketingLinkClick, resolveMarketingEventLink } from 'helpers/marketingTracking'
import { navKey } from 'views/AdminPage'
import { clearAdminProject, clearEditContent, setEditContent, setEditInline } from 'actions/adminActions'
import { contactText, icons, requestInfoFields, productConfig, BOT_USER_AGENTS } from 'app/constants'
import { organizationContent, organizationRoutes } from 'app/transformers'
import { ifError } from 'assert'
import { getWindowLocation } from 'helpers/config'
import { logger } from 'helpers/logger'

interface HomeBarProps {
    signUp: boolean,
    logIn: boolean,
    redirect: string,
}
export function HomeBar(props: HomeBarProps) {
    const { signUp, logIn, redirect } = { signUp: true, logIn: true, ...props }
    const dispatch = useAppDispatch()
    const loggedIn = useAppSelector((state: RootState) => state.user.loggedIn)
    const salescenter = useAppSelector((state: RootState) => state.app.salescenter)
    const rootPath = useAppSelector((state: RootState) => state.app.rootPath)
    const links = useAppSelector((state: RootState) => state.app.links)
    const screen = useAppSelector((state: RootState) => state.app.screen)
    const project = useAppSelector((state: RootState) => state.app.projectCurrent[0])
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const standaloneOrg = useAppSelector((state: RootState) => state.app.standaloneOrg)
    const standaloneApp = useAppSelector((state: RootState) => state.app.standaloneApp)
    const user = useAppSelector((state: RootState) => state.user.data)
    const config = useAppSelector((state: RootState) => state.app.config)
    const organizationVisited = useAppSelector((state: RootState) => state.app.organizationVisited)
    const customDomain = useAppSelector((state: RootState) => state.app.customDomain)
    const editInline = useAppSelector((state: RootState) => state.admin.editInline)
    const media = useAppSelector((state: RootState) => state.app.media)
    const isBot = useAppSelector((state: RootState) => state.app.analytics.isBot)

    const requireLoginSalescenter = project?.meta.requireLoginSalescenter
    const enableLoginSalescenter = project?.meta.enableLoginSalescenter

    function hasRole(x: Role) {
        return user && user.roles && user.roles.findIndex((y) => y == x) != -1
    }

    const altTheme = organization && organization.link == 'thepropertylinq'

    const adminPermissions = useAdminPermissions()

    const [hasEditPermissions, setHasEditPermissions] = useState(false)
    const [hasAdminPermissions, setHasAdminPermissions] = useState(false)
    const [canExpand, setCanExpand] = useState(null)
    const [expand, setExpand] = useState(true)
    const [showOrganizationOptions, setShowOrganizationOptions] = useState(false)
    const [OrganizationContentElement, setOrganizationContentElement] = useState(null)
    const [logInTriggered, setLogInTriggered] = useState(false)
    const [showRequestInfo, setShowRequestInfo] = useState(false)
    const [requestInfoConfig, setRequestInfoConfig] = useState(null)
    const page = usePage()

    // Memoize product images to avoid scanning media object repeatedly
    const productImages = useMemo(() => {
        if (organization?.link !== 'homegyde') {
            return {}
        }

        const imageMap = {}
        productConfig.items.forEach((product) => {
            const productMedia = Object.values(media).find((m) => m.link === product.image)
            if (productMedia) {
                imageMap[product.image] = productMedia.id
            }
        })
        return imageMap
    }, [media, organization?.link])

    let collapseMenu = false
    if (screen.isMobile && screen.size == ScreenSize.Mobile && screen.orientation == ScreenOrientation.Portrait) {
        collapseMenu = true
    }

    const bigLogo = !screen.isMobile && expand && canExpand && !(collapseMenu && showOrganizationOptions) && (links.pageLink != PageType.Clone) && links.appLink != PageType.Clone && (links.pageLink != PageType.Compare) && (links.baseRoute == null || (links.appLink != null && links.appLink != PageType.Compare && links.appLink != PageType.Favourite && links.appLink != PageType.Developments))
    

    useEffect(() => {
        if (salescenter || (project && !project.meta.enableRequestInfo)) {
            return
        }
        const toggleShowRequestInfo = (e) => {
            setShowRequestInfo(true)
            if (e?.detail?.config) {
                setRequestInfoConfig(e.detail.config)
            }
        }
        window.addEventListener(CustomEventType.ShowRequestInfo, toggleShowRequestInfo)
        return () => {
            window.removeEventListener(CustomEventType.ShowRequestInfo, toggleShowRequestInfo)
        }
    }, [])

    useEffect(() => {
        window.addEventListener(CustomEventType.RequestInfo, handleRequestInfo)
        return () => {
            window.removeEventListener(CustomEventType.RequestInfo, handleRequestInfo)
        }
    }, [organization, project])

    useEffect(() => {
        window.addEventListener(CustomEventType.ScrollAway, handleScroll)
        return () => {
            window.removeEventListener(CustomEventType.ScrollAway, handleScroll)
        }
    }, [expand])

    useEffect(() => {
        if (organization && !standaloneApp) {
            // setOrganizationContentElement(organizationContent(organization))
        }
    }, [organization])

    useEffect(() => {
        const pageType = page ? page.pageType : PageType.Info
        const newExpand = (pageType == null || pageType == PageType.Custom || pageType == PageType.Info || pageType == PageType.About || pageType == PageType.Launch) && links.baseRoute != PageType.Favourite
        
        setCanExpand(newExpand)
        setShowOrganizationOptions(false)
    }, [links])


    useEffect(() => {
        setHasEditPermissions(adminPermissions(project, organization, [UserRole.AppsEdit]))
        setHasAdminPermissions(adminPermissions(project, null, [UserRole.PricingEdit, UserRole.PricingView, UserRole.AvailabilityEdit, UserRole.AvailabilityView]))
    }, [project, loggedIn])

    // Redirect after login
    useEffect(() => {
        if (logInTriggered && loggedIn && user && user.admin && links.baseRoute != 'admin') {
            // handleAdmin()
        }
    }, [loggedIn, logInTriggered])

    function handleScroll(e) {
        setExpand(e.detail)
    }

    function handleHome() {
        dispatch(navigateAsync({ pageType: PageType.Home, options: { path: { baseRoute: '' } } }))
    }

    function handleSearch() {
        dispatch(navigateAsync({ pageType: '', options: { path: { baseRoute: PageType.Developments } } }))
    }

    function handleAboutUs() {
        dispatch(navigateAsync({ pageType: PageType.AboutUs }))
    }

    function handleContact() {
        dispatch(showPrompt({ ...PromptOptions.Booking, app: project }))
    }
    function handleAdmin() {
        handleEditApp()
        /*if (!standaloneApp) {
            dispatch(clearCurrent())
        }
        dispatch(navigateAsync({ pageType: PageType.Admin, options: { path: { baseRoute: '' } } }))*/
    }

    function handleLogIn() {
        setLogInTriggered(true)
        window.dispatchEvent(new CustomEvent(CustomEventType.LogIn, { detail: { redirect } }))
    }
    function handleLogInPIN() {
        setLogInTriggered(true)
        window.dispatchEvent(new CustomEvent(CustomEventType.LogInPIN, { detail: { redirect } }))
    }
    function handleSignUp() {
        setLogInTriggered(true)
        window.dispatchEvent(new CustomEvent(CustomEventType.Register, { detail: { redirect } }))
    }

    function handleUserSelect(x) {
        switch (x) {
            case 'logout':
                window.dispatchEvent(new CustomEvent(CustomEventType.LogOut))
                break
            case 'account':
                handleAdmin()
                break
            case 'edit':
                dispatch(setEditInline(!editInline))
            default:
                break
        }
    }

    function handleAppChange(x) {
        dispatch(setEditInline(false))
        dispatch(navigateAsync({ app: { meta: x }, pageType: '' }))
        window.dispatchEvent(new CustomEvent(CustomEventType.CloseAllMenus))
    }

    function handleEditApp() {
        // if (Object.values(AdminView).includes(links.pageLink)) {
        // localStorage.setItem(navKey(links.appLink), links.pageLink)
        // }
        let extraLink = links.pageLink
        // TODO: fix this confusing s / no s situation
        if (extraLink == PageType.Floorplan) {
            extraLink = AdminView.Floorplans
        }
        // Transform
        if (extraLink == 'upgrades' && !hasRole(UserRole.AppsEdit)) {
            extraLink = 'upgrades-config'
        }
    
        if (!Object.values(AdminView).includes(extraLink)) {
            extraLink = null
        }

        let dataLink = ''
        let pageType = PageType.Admin
        if(project) {
            dataLink = 'project/' + project.meta.link
        }

    
        dispatch(clearAdminProject())
        dispatch(navigateAsync({ 
            pageType,
            options: { 
                dataLink,
                extraLink, 
                path: { baseRoute: '' },
            },
        }))
        dispatch(initializeTheming())
    }

    function handleLoginDropdown(x) {
        switch (x) {
            case 'login':
                handleLogIn()
                break
            case 'signup':
                handleSignUp()
                break
        }
    }

    function handleEndSession() {
        dispatch(showPrompt({ type: PromptType.Confirm, title: 'End Session', message: 'Are you sure you want to log out?', confirmMessage: 'Yes', cancelMessage: 'No' }))
            .then((x) => {
                if (x.payload) {
                    window.dispatchEvent(new CustomEvent(CustomEventType.LogOut))
                }
            })
    }

    function handleRequestInfo(e) {
        const detail = e?.detail ? e.detail : {}
        let options = {}

        let infoTarget = project ? project.meta.name : organization.name

        let formOptions = {
            submitToHubspot: project?.meta.hubspotTrackingId != null,
            analyticsEventTypeId: AnalyticsEvent.RequestInformation,
        }
        if (requestInfoConfig) {
            const { fields, ...configFields } = requestInfoConfig
            formOptions = { ...formOptions, ...configFields }
        }
        if (detail.formOptions) {
            formOptions = { ...formOptions, ...detail.formOptions }
        }

        options = {
            ...options,
            ...PromptOptions.RequestInfo,
            app: project,
            organization,
            submitMessage: 'Request Info',
            ...detail,
            // Keep the merged defaults (HubSpot delivery, analytics, and the
            // selected marketing label) rather than replacing them with the
            // partial options supplied by an individual CTA.
            formOptions,
        }
        if(requestInfoConfig?.fields) {
            options.fields = requestInfoConfig.fields
        } else {
            options.fields = requestInfoFields(user, infoTarget)
        }


        if (organization && organization.consent) {
            options.fields.push({
                id: 'consent',
                label: organization.consent,
            })
        }

        if (detail?.fields) {
            // Only include
            options.fields = options.fields.filter((x) => detail.fields.includes(x.id))
        }

        dispatch(showPrompt(options))

    }
    function getLoginButtons() {
        if (salescenter || loggedIn) {
            return null
        }
        if (screen.isMobile) {
            const items = []
            if (logIn) {
                items.push({ value: 'login', text: 'Sign In' })
            }
            // if (signUp) {
            // items.push({ value: 'signup', text: 'Sign Up' })
            // }
            return <DropdownMenu
                text={null}
                contentAnimation="fadeInUp"
                buttonClass={altTheme ? 'no-bg' : ''}
                icon={icons.user}
                asList={false}
                items={items}
                onChange={handleLoginDropdown} />
        } else {
            const elements = []
            if (logIn) {
                elements.push(<Button onClick={handleLogIn}>Sign In</Button>)
                // elements.push(<Button noBg onClick={handleLogIn}>Log In</Button>)
            }
            // if (signUp) {
            //     elements.push(<Button noBg={altTheme} onClick={handleSignUp}>Sign Up</Button>)
            // }
            return elements
        }
    }

    function getOrganizationOptions() {
        if (organization && !standaloneApp) {
            let prefix = '/'
            if (!customDomain.organization && organization?.link != 'homegyde') {
                prefix += `${organization.link}/`
            }
            const orgPages = organizationRoutes(organization)
            const visibleOrgPages = orgPages.filter((x) => x.showInMenu && (!x.draft || hasEditPermissions))

            // Get products dropdown items for HomeGyde
            const getProductsDropdownItems = () => {
                if (organization?.link !== 'homegyde') {
                    return []
                }

                const productItems = productConfig.items.map((product, idx) => {
                    // Use memoized product images
                    const mediaId = productImages[product.image]

                    return {
                        value: product.title.toLowerCase().replace(/\s+/g, '-'),
                        element: (
                            <Link 
                                to={product.link} 
                                key={idx} 
                                className="product-dropdown-item"
                                onClick={() => {
                                    // Close the dropdown menu when product is clicked
                                    window.dispatchEvent(new CustomEvent(CustomEventType.CloseAllMenus))
                                }}
                            >
                                {mediaId && (
                                    <div className="product-image">
                                        <Media 
                                            mediaId={mediaId} 
                                            organization={organization}
                                            thumb={true}
                                            thumbSize={128}
                                        />
                                    </div>
                                )}
                                <div className="product-content">
                                    <span><Icon noBg primary icon="fas fa-chevron-right"/><h4>{product.title}</h4></span>
                                    <p>{product.subtitle}</p>
                                </div>
                            </Link>
                        )
                    }
                })

                // Add header as first item and wrap product items in a container
                return [
                    {
                        element: (
                            <div className="product-dropdown-header">
                                <h3>{productConfig.title}</h3>
                                <p>{productConfig.subtitle}</p>
                            </div>
                        )
                    },
                    {
                        element: (
                            <div className="product-dropdown-list">
                                {productItems.map(item => item.element)}
                            </div>
                        )
                    }
                ]
            }

            const showProducts = organization?.link === 'homegyde'

            // On mobile with collapsed menu and showing options, only render logo in topbar
            // The menu content will be rendered via portal
            if (collapseMenu && showOrganizationOptions) {
                return <React.Fragment>
                    {canExpand != null && <Logo onClick={handleHome} expanded={bigLogo} />}
                </React.Fragment>
            }

            // For bots on desktop: flatten root-level organization options and prepend them
            if (isBot && !collapseMenu && showProducts) {
                const flattenedOptions = [
                    ...visibleOrgPages.map((x) => (
                        <Link to={`${prefix}${x.link}`} key={x.link}>
                            <Button 
                                className={(links.baseRoute == x.link || links.appLink == x.link || links.pageLink == x.link) ? 'selected' : ''} 
                                noBg 
                                onClick={() => setShowOrganizationOptions(false)} 
                                propogate
                            >
                                {x.name}
                            </Button>
                        </Link>
                    ))
                ]

                return <React.Fragment>
                    {canExpand != null && <Logo onClick={handleHome} expanded={bigLogo} />}
                    {!collapseMenu && (
                        <div className={`column organization-options`}>
                            {productConfig.items.map((product, idx) => (
                                <Link 
                                    to={product.link} 
                                    key={`bot-product-${idx}`}
                                >
                                    <Button noBg onClick={() => setShowOrganizationOptions(false)} propogate>
                                        {product.title}
                                    </Button>
                                </Link>
                            ))}
                            {flattenedOptions}
                        </div>
                    )}
                </React.Fragment>
            }

            // Normal rendering (desktop or mobile with menu closed)
            return <React.Fragment>
                {canExpand != null && <Logo onClick={handleHome} expanded={bigLogo} />}
                {(!collapseMenu || showOrganizationOptions) &&
                    <div className={`column organization-options${screen.isMobile ? ' animate__animated animate__fadeInDown animate__fast' : ''}`}>
                        {showProducts && !collapseMenu && (
                            <DropdownMenu
                                text="HomeGyde System"
                                items={getProductsDropdownItems()}
                                onChange={(x) => {
                                    // Handle product selection
                                    logger.info('Product selected:', x)
                                }}
                                closeOnSelect={false}
                                buttonClass="no-bg"
                                className="products-dropdown"
                                contentAnimation="fadeIn"
                            />
                        )}
                        {!isBot && visibleOrgPages.map((x) => {
                            return <Link to={`${prefix}${x.link}`} key={x.link}>
                                <Button className={(links.baseRoute == x.link || links.appLink == x.link || links.pageLink == x.link) ? 'selected' : ''} noBg onClick={() => setShowOrganizationOptions(false)} propogate>{x.name}</Button>
                            </Link>
                        })}
                        {isBot && visibleOrgPages.map((x) => {
                            return <Link to={`${prefix}${x.link}`} key={x.link}>
                                <Button className={(links.baseRoute == x.link || links.appLink == x.link || links.pageLink == x.link) ? 'selected' : ''} noBg onClick={() => setShowOrganizationOptions(false)} propogate>{x.name}</Button>
                            </Link>
                        })}
                    </div>
                }
            </React.Fragment>
        } else {
            return <Logo onClick={handleHome} expanded={ bigLogo } />
        }
        return null
    }

    function renderMobileMenuPortal() {
        if (!organization || standaloneApp) {
            return null
        }

        let prefix = '/'
        if (!customDomain.organization && organization?.link != 'homegyde') {
            prefix += `${organization.link}/`
        }
        const orgPages = organizationRoutes(organization)
        const visibleOrgPages = orgPages.filter((x) => x.showInMenu && (!x.draft || hasEditPermissions))
        const showProducts = organization?.link === 'homegyde'

        return (
            <div className="mobile-organization-options">
                <div className="column organization-options animate__animated animate__fadeInDown animate__fast">
                    {showProducts && (
                        <div className="products-section">
                            <div className="products-header">
                                <h3>{productConfig.title}</h3>
                                <p>{productConfig.subtitle}</p>
                            </div>
                            <div className="products-list-mobile">
                                {productConfig.items.map((product, idx) => {
                                    const mediaId = productImages[product.image]
                                    return (
                                        <Link 
                                            to={product.link} 
                                            key={idx} 
                                            className="product-item-mobile"
                                            onClick={() => {
                                                setShowOrganizationOptions(false)
                                                window.dispatchEvent(new CustomEvent(CustomEventType.CloseAllMenus))
                                            }}
                                        >
                                            {mediaId && (
                                                <div className="product-image">
                                                    <Media 
                                                        mediaId={mediaId} 
                                                        organization={organization}
                                                        thumb={true}
                                                        thumbSize={128}
                                                    />
                                                </div>
                                            )}
                                            <div className="product-content">
                                                <span><Icon noBg primary icon="fas fa-chevron-right"/><h4>{product.title}</h4></span>
                                                <p>{product.subtitle}</p>
                                            </div>
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    {visibleOrgPages.map((x) => {
                        return <Link to={`${prefix}${x.link}`} key={x.link}>
                            <Button className={(links.baseRoute == x.link || links.appLink == x.link || links.pageLink == x.link) ? 'selected' : ''} noBg onClick={() => setShowOrganizationOptions(false)} propogate>{x.name}</Button>
                        </Link>
                    })}
                </div>
            </div>
        )
    }

    const extraOptionsBefore = []
    const extraOptionsAfter = []
    const tracking = project?.marketingTracking || project?.marketing_tracking || project?.meta?.marketingTracking || project?.meta?.marketing_tracking || organization?.marketingTracking || organization?.marketing_tracking
    const hasConfiguredEventLinks = (tracking?.eventLinks || tracking?.event_links || []).length > 0
    const scheduleAction = (tracking?.conversionActions || tracking?.conversion_actions || []).find((action) => action.enabled && (action.keyName || action.key_name) === 'schedule_tour' && (action.eventName || action.event_name) === 'form_submit')
    const requestInfoLink = resolveMarketingEventLink(project, organization, 'request_info')
    const scheduleLink = resolveMarketingEventLink(project, organization, 'schedule_tour')
    const tourUrl = marketingScheduleDestination(scheduleLink)
    const hasTourUrl = isHttpUrl(tourUrl)
    const phoneLink: any = resolveMarketingEventLink(project, organization, 'phone')
    const phoneNumber = phoneLink?.destination || (!hasConfiguredEventLinks ? project?.meta.bookingPhone : null)

    if (showRequestInfo) {
        if (phoneNumber) {
            extraOptionsBefore.push(<a href={`tel:${phoneNumber}`} key="call" onClick={() => reportMarketingEvent({ app: project, organization, eventName: phoneLink?.eventName || phoneLink?.event_name || 'phone_click', label: phoneLink?.label || 'sales_phone', conversionActionId: phoneLink?.conversionActionId || phoneLink?.conversion_action_id, source: 'cta' })} className="animate__animated animate__fadeInUp animate__delay-2s animate__slow">
                <Button noBg rounded>{fnc.phoneNumber(phoneNumber)}</Button>
            </a>)
        }
        extraOptionsBefore.push(<Button key="request" className="animate__animated animate__fadeInUp animate__delay-2s animate__slow" icon={icons.paperPlane} onClick={() => handleRequestInfo({ detail: { formOptions: marketingFormOptions(requestInfoLink) } })} tertiary rounded>{requestInfoLink?.ctaText || 'Request Info'}</Button>)
    }

    // A configured Schedule a Tour action is an explicit publishing choice and
    // should not wait for the optional navigation tutorial to finish.
    if (!screen.isMobile && (hasTourUrl || (!salescenter && project?.meta.enableRequestInfo && (scheduleLink || (!hasConfiguredEventLinks && scheduleAction))))) {
        const scheduleOptions = scheduleLink ? marketingFormOptions(scheduleLink) : { marketingTrackingEnabled: true, marketingEventName: 'form_submit', marketingLabel: 'schedule_tour', marketingConversionActionId: scheduleAction?.id || scheduleAction?.keyName || scheduleAction?.key_name }
        extraOptionsBefore.push(hasTourUrl
            ? <a key="schedule-tour" href={tourUrl} target="_blank" rel="noopener noreferrer" onClick={() => reportMarketingLinkClick(project, organization, scheduleLink)} className="animate__animated animate__fadeInUp animate__delay-2s animate__slow"><Button icon={icons.calendar} tertiary rounded>{scheduleLink?.ctaText || scheduleLink?.cta_text || 'Schedule a Tour'}</Button></a>
            : <Button key="schedule-tour" className="animate__animated animate__fadeInUp animate__delay-2s animate__slow" icon={icons.calendar} onClick={() => handleRequestInfo({ detail: { formOptions: scheduleOptions } })} tertiary rounded>{scheduleLink?.ctaText || scheduleLink?.cta_text || 'Schedule a Tour'}</Button>)
    }

    if (loggedIn && salescenter) {
        extraOptionsBefore.push(<Button key="logout" className="animate__animated animate__fadeInUp animate__fast" icon={icons.signOut} onClick={handleEndSession} tertiary rounded>End Session</Button>)
    }

    if ((hasEditPermissions || hasAdminPermissions) && links.appLink != null && project && project.meta.link == links.appLink) {
        if (screen.isMobile) {
            extraOptionsBefore.push(<IconButton key="edit" icon={icons.pen} onClick={handleEditApp} />)
        } else {
            extraOptionsBefore.push(<Button key="edit" onClick={handleEditApp}>{hasEditPermissions ? 'Project Admin' : 'Administration'}</Button>)
        }
    }

    const split = getWindowLocation().pathname.split('/').filter((x) => x.length > 0)
    const lastLink = split.length > 0 ? split[split.length - 1] : null
    if (/*organizationVisited && getWindowLocation().pathname !== rootPath &&*/ !standaloneApp && lastLink !== PageType.Developments && !standaloneApp && (!(standaloneOrg && organization.link != 'thepropertylinq') || (links.baseRoute == PageType.Developments && links.appLink != null))) {
        if (organization?.pages?.find((x) => x.link == PageType.Developments) && organization?.enableBrowseCommunities !== false) {
            extraOptionsBefore.push(<React.Fragment key="browse">
                {collapseMenu && <IconButton noBg={altTheme} icon={icons.map} onClick={handleSearch} />}
                {!collapseMenu && <Button noBg={altTheme} icon={icons.map} onClick={handleSearch}>Browse Communities</Button>}
            </React.Fragment>)
        }
    }

    {/*if (standaloneOrg && !standaloneApp) {
        extraOptionsAfter.push(<Button key="request" tertiary onClick={handleContact}>{contactText}</Button>)
    }*/}

    const visibleOrgPages = organizationRoutes(organization)?.filter((x) => x.showInMenu && !x.draft)
    if (collapseMenu && visibleOrgPages.length > 0) {
        extraOptionsAfter.push(<IconButton key="bars" noBg={altTheme} icon={icons.hamburger} onClick={() => setShowOrganizationOptions(!showOrganizationOptions)} />)
    }

    let menuItems = []
    if (loggedIn) {
        menuItems = [
            {
                element: <span className="heading row">
                    <div className="column">
                        <div className="row">
                            <span>{user.name}</span>
                        </div>
                        {salescenter && <div className="row">
                            <span>{user.email}</span>
                        </div>}
                        {salescenter && <div className="row">
                            <span>{fnc.phoneNumber(user.phone)}</span>
                        </div>}
                    </div>
                </span>
            },
            {
                text: 'Log Out',
                value: 'logout',
            },
            {
                text: user.admin ? 'Admin Panel' : 'Account',
                value: 'account',
            },

        ]
        if (user?.admin) {
            // If project is included in users approved apps
            const orgPermissions = !project && adminPermissions(null, organization, [UserRole.AppsEdit])
            if (orgPermissions || (project && project.meta.id == user?.apps?.find((x) => x.id == project.meta.id)?.id)) {
                if (editInline) {
                    menuItems.push({ value: 'edit', text: 'Stop Editing' })
                } else {
                    menuItems.push({ value: 'edit', text: 'Edit Page' })
                }
            }
        }
        if (user.admin) {
            let appItems = []
            user.apps.forEach((userApp) => {
                // If organization, make sure the app is in the organization
                if (customDomain.organization) {
                    const appMap = new Set(organization.apps)
                    if (organization && organization.apps && !appMap.has(userApp.id)) {
                        return
                    }
                }
                const selected = project && project.meta.id == userApp.id
                if (userApp) {
                    appItems.push({
                        element: <Button className={selected ? 'selected' : ''} onClick={!selected ? () => handleAppChange(userApp) : null}>{userApp.name}</Button>
                    })
                }
            })
            if (appItems.length > 0) {
                menuItems.push({
                    element: <span className="heading">Projects</span>
                })
                menuItems = menuItems.concat(appItems)
            }
        }
    }


    return <TopBar className={`mainbar animate__animated animate__fadeIn${bigLogo ? ' expanded' : ''}`}>
        <div className="row">
            <div className="options left">
                {getOrganizationOptions()}
            </div>
            <div className="options right">
                {extraOptionsBefore}
                {extraOptionsBefore.length > 0 && <div className="options-divider" />}
                {/* {!standaloneApp && <FavouriteNavButton noBg={altTheme} />} */}
                {<FavouriteNavButton noBg={altTheme} />}
                {getLoginButtons()}
                {salescenter && !loggedIn && !requireLoginSalescenter && enableLoginSalescenter && <Button noBg={altTheme} onClick={handleLogInPIN}>Login with PIN</Button>}
                {loggedIn && <DropdownMenu
                    icon={icons.user}
                    openIcon={icons.times}
                    contentAnimation="fadeInUp"
                    buttonClass={altTheme ? 'no-bg' : ''}
                    asList={false}
                    onChange={handleUserSelect}
                    items={menuItems}
                />}

                {extraOptionsAfter.length > 0 && <div className="options-divider" />}
                {extraOptionsAfter}
                {/* <Button noBg onClick={handleAboutUs}>About Us</Button> */}
            </div>
            {/*<div className="options left">
                {location.pathname.length > 1 && <BackButton onClick={handleHome}/>}
            </div>*/}
        </div>
        {screen.isMobile && showOrganizationOptions && ReactDOM.createPortal(
            renderMobileMenuPortal(),
            document.getElementById('app')
        )}
    </TopBar>
}
